import type { EnvironmentSetting } from '@/services/environmentSettingsApi';

// =============================================================================
// TYPES
// =============================================================================

export interface EditableSetting {
  id: string;
  name: string;
  value: string;
  isNew?: boolean;
  isDeleted?: boolean;
  originalName?: string;
  originalValue?: string;
}

export type ValidationErrorKind = 'empty' | 'invalid' | 'duplicate';

export interface ValidationError {
  kind: ValidationErrorKind;
  message: string;
  /** For duplicates, the id of the first (pre-existing) row with the same name. */
  duplicateOfId?: string;
}

/**
 * The payload shape sent to the backend on save.
 */
export interface PutSetting {
  name: string;
  value: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Environment setting key name rules: letters/digits/underscores/dots; must not start with a digit or dot. */
export const ENV_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_.]*$/;

export const MASK = '••••••••••';

/** Copy-button "Copied!" flash duration, in ms. */
export const COPIED_FLASH_MS = 1500;

/**
 * Duration of the row-highlight flash used by "Jump to existing" to draw the
 * user's eye to the destination row after scrolling. Kept short so the
 * highlight is a pulse, not a lingering state.
 */
export const ROW_FLASH_MS = 1500;

export const INVALID_KEY_MESSAGE =
  'Invalid key name — use letters, digits, underscores, and dots; cannot start with a digit or dot.';

export const POSIX_KEY_HINT = 'Letters, digits, underscores, and dots only. Must not start with a digit or dot.';

// =============================================================================
// PURE HELPERS
// =============================================================================

/** Build editable row state from the loaded server settings. */
export const settingsToEditable = (settings: EnvironmentSetting[]): EditableSetting[] =>
  settings.map((s) => ({
    id: crypto.randomUUID(),
    name: s.name,
    value: s.value ?? '',
    originalName: s.name,
    originalValue: s.value ?? '',
  }));

/**
 * True when an existing row's name or value differs from its loaded value.
 * Used for both dirty-tracking and the "Modified" source-cell label.
 */
export const isModified = (item: EditableSetting): boolean =>
  item.name !== item.originalName || item.value !== item.originalValue;

/** The exact Logic Apps reference syntax for app settings. */
export const formatReference = (name: string): string => `@appsetting('${name}')`;

/**
 * Compute per-row validation errors for the given items.
 *
 * Collisions are **case-sensitive**: env var names are POSIX, so `Foo` and
 * `foo` are two distinct variables and must be flagged as duplicates only
 * when identical.
 *
 * Deleted rows are ignored (they don't contribute to duplicate detection and
 * don't receive errors).
 * 
 * Canonical-row priority. Lower wins. Picks the row least likely to be the
 * one the user just edited, so rename collisions flag the *renamed* row
 * rather than the stable one that has quietly held the name all along.
 */
const canonicalPriority = (item: EditableSetting): number => {
  if (item.isNew) return 2;
  return item.name === item.originalName ? 0 : 1;
};

export const buildValidationErrors = (
  items: EditableSetting[],
): Record<string, ValidationError> => {
  const errors: Record<string, ValidationError> = {};
  const nonDeletedItems = items.filter((i) => !i.isDeleted);

  // Pick the canonical row for each name by lowest priority; ties go to
  // first-seen so behaviour stays stable as the list re-renders.
  const nameToFirstId = new Map<string, string>();
  const nameToPriority = new Map<string, number>();
  for (const item of nonDeletedItems) {
    if (!item.name) continue;
    const p = canonicalPriority(item);
    const incumbent = nameToPriority.get(item.name);
    if (incumbent === undefined || p < incumbent) {
      nameToFirstId.set(item.name, item.id);
      nameToPriority.set(item.name, p);
    }
  }

  // Emit errors for every row that isn't the canonical one.
  for (const item of nonDeletedItems) {
    if (!item.name.trim()) {
      errors[item.id] = { kind: 'empty', message: 'Name is required' };
      continue;
    }
    if (!ENV_KEY_PATTERN.test(item.name)) {
      errors[item.id] = { kind: 'invalid', message: INVALID_KEY_MESSAGE };
      continue;
    }
    const firstId = nameToFirstId.get(item.name);
    if (firstId && firstId !== item.id) {
      errors[item.id] = {
        kind: 'duplicate',
        message: `A setting named "${item.name}" already exists.`,
        duplicateOfId: firstId,
      };
    }
  }

  return errors;
};

export const buildPutSettings = (items: EditableSetting[]): PutSetting[] => {
  const result: PutSetting[] = [];
  for (const item of items) {
    if (item.isDeleted) continue;
    result.push({ name: item.name, value: item.value });
  }
  return result;
};

/**
 * Bucketed counts of pending changes for telemetry. The shape is
 * intentionally name/value-free — env var identifiers are treated as
 * potentially sensitive (they can reveal infrastructure details — e.g.
 * `STRIPE_LIVE_SECRET_KEY`) so we only send counts.
 */
export interface ChangeCounts {
  added: number;
  updated: number;
  renamed: number;
  deleted: number;
  total: number;
}

/** Compute add/update/rename/delete counts across all editable items. */
export const computeChangeCounts = (items: EditableSetting[]): ChangeCounts => {
  let added = 0;
  let updated = 0;
  let renamed = 0;
  let deleted = 0;

  for (const item of items) {
    if (item.isDeleted) {
      if (item.originalName) deleted++;
      continue;
    }
    if (item.isNew) {
      added++;
      continue;
    }
    const nameChanged = !!item.originalName && item.name !== item.originalName;
    const valueChanged = item.value !== item.originalValue;
    if (nameChanged) {
      renamed++;
    } else if (valueChanged) {
      updated++;
    }
  }

  return { added, updated, renamed, deleted, total: added + updated + renamed + deleted };
};

/**
 * Rebuild items with clean dirty-tracking state after a successful save.
 *
 * - Rows marked `isDeleted` are dropped (the server no longer has them).
 * - `isNew` is cleared (the server now owns the row).
 * - `originalName` / `originalValue` are set to the current values so
 *   `isModified()` returns `false`.
 *
 * This lets the component reset `isDirty` synchronously on save success
 * rather than depending on the async React Query refetch, which can be
 * skipped by structural sharing when the response is identical.
 */
export const resetItemsAfterSave = (items: EditableSetting[]): EditableSetting[] =>
  items
    .filter((item) => !item.isDeleted)
    .map(({ isNew: _isNew, isDeleted: _isDeleted, ...item }) => ({
      ...item,
      originalName: item.name,
      originalValue: item.value,
    }));
